import type { ListResponse, ObjectResponse } from '../../../common/types';
import { annotationToolApiV1, getAnnotationToolBasePath } from '../../../lib';
import type { AnnotationTaskResponse, Note } from '../../annotator-queue/types/responseTypes';
import type { Annotation } from '../../workspace-common/types/responseTypes';
import { ANNOTATION_TAG } from '../constants';
import type {
  AddSpanRequest,
  BulkCreateAnnotationsRequest,
  CreateAnnotationRequest,
  DeleteAnnotationRequest,
  FinalizeAnnotationRequest,
  UpdateAnnotationAttributesRequest,
} from '../types/request';
import type { AnnotationWorkspaceResponse, DeleteAnnotationResponse } from '../types/response';

export const annotationApiSlice = annotationToolApiV1
  .enhanceEndpoints({
    addTagTypes: [ANNOTATION_TAG],
  })
  .injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
      getEncounterNotes: builder.query<
        ListResponse<Note>,
        {
          taskId: string;
        }
      >({
        query: ({ taskId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/encounters/${taskId}/notes`;
        },
        providesTags: [ANNOTATION_TAG],
      }),

      getEncounterDetails: builder.query<
        ObjectResponse<AnnotationTaskResponse>,
        {
          taskId: string;
        }
      >({
        query: ({ taskId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/encounters/${taskId}`;
        },
        providesTags: [ANNOTATION_TAG],
      }),

      getAnnotationWorkspace: builder.query<
        ObjectResponse<AnnotationWorkspaceResponse>,
        {
          taskId: string;
        }
      >({
        query: ({ taskId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/work-queue/tasks/${taskId}/annotation-workspace`;
        },
        providesTags: [ANNOTATION_TAG],
      }),
      addAnnotationSpans: builder.mutation<
        ObjectResponse<{ annotationId: string; added: boolean }>,
        AddSpanRequest
      >({
        query: ({ annotationId, spans }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/annotations/${annotationId}/spans`,
            method: 'POST',
            body: {
              spans,
            },
          };
        },

        invalidatesTags: [ANNOTATION_TAG],
      }),
      createAnnotation: builder.mutation<ObjectResponse<Annotation>, CreateAnnotationRequest>({
        query: ({ taskId, conceptId, attributes, spans }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/work-queue/tasks/${taskId}/annotations`,
            method: 'POST',
            body: {
              conceptId,
              attributes,
              spans: spans || [],
            },
          };
        },
        invalidatesTags: [ANNOTATION_TAG],
      }),
      updateAnnotationAttributes: builder.mutation<
        ObjectResponse<Annotation>,
        UpdateAnnotationAttributesRequest
      >({
        query: ({ annotationId, attributes, spans, evidenceIdsToDelete }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/annotations/${annotationId}/attributes`,
            method: 'PUT',
            body: {
              attributes,
              spans: spans || [],
              evidenceIdsToDelete,
            },
          };
        },
        invalidatesTags: [ANNOTATION_TAG],
      }),
      deleteAnnotation: builder.mutation<
        ObjectResponse<DeleteAnnotationResponse>,
        DeleteAnnotationRequest
      >({
        query: ({ annotationId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/annotations/${annotationId}`,
            method: 'DELETE',
          };
        },

        invalidatesTags: [ANNOTATION_TAG],
      }),
      bulkCreateAnnotations: builder.mutation<
        ObjectResponse<{ annotations: Annotation[] }>,
        BulkCreateAnnotationsRequest
      >({
        query: ({ taskId, annotations }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/work-queue/tasks/${taskId}/annotations/bulk`,
            method: 'POST',
            body: {
              annotations,
            },
          };
        },

        invalidatesTags: [ANNOTATION_TAG],
      }),
      finalizeAnnotation: builder.mutation<
        ObjectResponse<AnnotationTaskResponse>,
        FinalizeAnnotationRequest
      >({
        query: ({ taskId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/work-queue/tasks/${taskId}/finalize`,
            method: 'POST',
          };
        },

        invalidatesTags: [ANNOTATION_TAG],
      }),
    }),
  });

export const {
  useGetEncounterNotesQuery,
  useGetEncounterDetailsQuery,
  useGetAnnotationWorkspaceQuery,
  useAddAnnotationSpansMutation,
  useCreateAnnotationMutation,
  useUpdateAnnotationAttributesMutation,
  useDeleteAnnotationMutation,
  useBulkCreateAnnotationsMutation,
  useFinalizeAnnotationMutation,
} = annotationApiSlice;
